import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { onboardingRestrictedGuard } from './core/guards/onboarding.guard';
import { permissionGuard } from './core/guards/role.guard';

export const appRoutes: Routes = [
  {
    path: 'login',
    loadChildren: () =>
      import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: 'signup',
    loadChildren: () =>
      import('./features/auth/signup.routes').then((m) => m.SIGNUP_ROUTES),
  },
  {
    // TV/kiosk "Live Patients Queue" board — kept top-level and unguarded (no authGuard, no
    // ShellComponent sidebar/topbar) so a browser tab left open on a waiting-room display
    // isn't kicked to /login; the underlying REST call still requires the JWT the
    // authInterceptor attaches from whichever staff account last signed in on that browser.
    path: 'display',
    loadChildren: () =>
      import('./features/queue-display/queue-display.routes').then((m) => m.QUEUE_DISPLAY_ROUTES),
  },
  {
    // Matches the link emailed/WhatsApped by AppointmentController/RecurringJobs/
    // BackgroundMailerJobs on PlusPediatric deployments: https://<domain>/#/patient-milestone?token=...
    // Kept top-level (outside ShellComponent) and unguarded, same pattern as /resetpassword below —
    // the parent opening this link is typically not signed in on this browser. The backend's
    // MilestoneTokenAuthorizeAttribute (not authGuard) is what actually gates access here, scoped
    // to this one token rather than a logged-in session.
    path: 'patient-milestone',
    loadChildren: () =>
      import('./features/patient-milestone/patient-milestone.routes').then((m) => m.PATIENT_MILESTONE_ROUTES),
  },
  {
    // PlusRadiology DICOM viewer — full-screen workspace with its own header/toolbar, so it's
    // kept top-level (outside ShellComponent, no sidebar/topbar) like /display above, but unlike
    // that kiosk route this one shows real patient imaging and stays behind authGuard.
    path: 'dicom-viewer/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/dicom-viewer/dicom-viewer.component').then((m) => m.DicomViewerComponent),
  },
  {
    // Matches the link mailed by the forgot-password / "Reset Password" (users-permissions)
    // emails: https://<domain>/#/resetpassword?AccessToken=... — kept top-level (not nested
    // under /login) so it matches that URL exactly, and left unguarded since the recipient
    // is typically not signed in on this browser.
    path: 'resetpassword',
    loadComponent: () =>
      import('./features/auth/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/auth/components/reset-password/reset-password.component'
          ).then((m) => m.ResetPasswordComponent),
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/shell.component').then((m) => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        data: { permission: { module: 'Dashboard' } },
        loadChildren: () =>
          import('./features/dashboard/dashboard.route').then(
            (m) => m.dashboardRoutes,
          ),
      },
      {
        path: 'appointments',
        canActivate: [onboardingRestrictedGuard, permissionGuard],
        data: { permission: { module: 'Appointments' } },
        loadChildren: () =>
          import('./features/appointments/appointments.routes').then(
            (m) => m.appointmentRoutes,
          ),
      },
      {
        path: 'patients',
        canActivate: [onboardingRestrictedGuard, permissionGuard],
        data: { permission: { module: 'Patients' } },
        loadChildren: () =>
          import('./features/patients/patients.routes').then(
            (m) => m.patientsRoutes,
          ),
      },
      {
        // Admissions/Bed Board/Nursing Station/Doctor Station/Ward Setup — Pluspedia (pediatric
        // build) only; the sidebar only links here when environment.environmentName === 'pluspedia'
        // (see sidebar.component.ts). Grouped under one lazy chunk via an empty-path parent since
        // each screen is its own top-level route, not nested under a shared URL segment.
        path: '',
        loadChildren: () =>
          import('./features/inpatient/inpatient.routes').then(
            (m) => m.INPATIENT_ROUTES,
          ),
      },
      {
        path: 'billing',
        canActivate: [onboardingRestrictedGuard, permissionGuard],
        data: { permission: { module: 'Billing' } },
        loadChildren: () =>
          import('./features/billing/billing.routes').then(
            (m) => m.billingRoutes,
          ),
      },
      {
        path: 'reports',
        canActivate: [onboardingRestrictedGuard, permissionGuard],
        data: { permission: { module: 'Reports' } },
        loadChildren: () =>
          import('./features/reports/reports.routes').then(
            (m) => m.REPORTS_ROUTES,
          ),
      },
      {
        path: 'help',
        canActivate: [onboardingRestrictedGuard, permissionGuard],
        data: { permission: { module: 'Help' } },
        loadChildren: () =>
          import('./features/help/help.route').then((m) => m.HELP_ROUTES),
      },
      {
        path: 'settings',
        canActivate: [onboardingRestrictedGuard, permissionGuard],
        data: { permission: { module: 'Setup' } },
        loadChildren: () =>
          import('./features/settings/settings.routes').then(
            (m) => m.settingsRoutes,
          ),
      },
      {
        path: 'profile',
        canActivate: [onboardingRestrictedGuard],
        loadChildren: () =>
          import('./features/profile/profile.route').then(
            (m) => m.PROFILE_ROUTES,
          ),
      },
      { // visible; only the main content area shows the "no permission" message.
        path: 'access-denied',
        loadComponent: () =>
          import('./features/access-denied/access-denied.component').then(
            (m) => m.AccessDeniedComponent,
          ),
      },
      { path: '**', redirectTo: 'dashboard' },
    ],
  },
];
